/*
Geolocation helper.

⚠️ Ne PAS confondre deux « 30 m » (la confusion a produit le bug géoloc
« perte de Location 5-10 s » du smoke 2026-06-10) :
- 30 m de DISTANCE à l'arbre = le gate de proximité capture
  (`kMaxArbreMeters`, parité Android `MAX_DISTANCE_M`).
- 30 m d'ACCURACY GPS = un objectif de convergence du poll one-shot
  `requestFreshFix` UNIQUEMENT. Android n'applique AUCUN filtre
  d'accuracy à la disponibilité (`captureAvailability` accepte tout fix
  d'âge ≤ 30 s ; le readout chasse prend `currentLocation` sans aucun
  filtre). Filtrer la disponibilité sur l'accuracy fait apparaître
  « Active le GPS » dès que la précision fluctue au-dessus de 30 m en
  déplacement urbain, alors que le GPS est parfaitement actif.

Two-tier resolution:
1. **Cached fix** pushed by the MapLibre GeolocateControl via `recordFix`
   each time it gets a callback. This is what the user sees as the blue
   dot — using it for captures means the geolocation is already warm and
   the capture is instant.
2. **Native API fallback** with a hardened outer timeout. iOS Safari in
   standalone PWA mode has a long-standing bug where
   `getCurrentPosition` can hang forever even with a `timeout` option;
   we layer our own setTimeout that rejects the Promise so the capture
   flow's status callback always fires.

iOS Safari quirks:
- Geolocation requires a user gesture on first use to surface the prompt
  reliably. Call this from inside a click handler.
- Background updates do NOT work in standalone PWA mode. We only ever
  try to get a foreground fix.
*/

export const kMaxGpsAgeMs: number = 30_000  // a fix older than this is unusable for availability gates — parité Android MAX_GPS_AGE_MS (CaptureLauncher.kt)
const kMinAccuracyMeters: number = 30  // target accuracy for the requestFreshFix convergence poll ONLY — never a gate on availability (see header)
const kPollTimeoutMs: number = 10_000  // outer JS-level poll window — wait up to this long for accuracy to converge under kMinAccuracyMeters
const kEarthRadiusMeters: number = 6_371_000  // for haversine distance computations
const kCachedFixTtlMs: number = 24 * 3600 * 1000  // disk-cached fix expires after 24 h; large enough to span typical away-from-app windows
const kCachedFixStorageKey: string = 'canopaname.lastFix.v1'  // localStorage key holding the JSON-serialised last fix

export interface FreshFix
{
	lat: number
	lon: number
	accuracyMeters: number
	timestamp: number
}

let fLatestFix: FreshFix | null = null  // most recent fix from any source, used to short-circuit capture flow
const fListeners = new Set<(inFix: FreshFix | null) => void>()  // observers for live distance widgets etc.
let fPermissionDenied = false  // true après un PERMISSION_DENIED (code 1) ; réarmé par tout fix réussi
const fDeniedListeners = new Set<() => void>()  // observers du refus de permission (toast MapScreen)

/*
Record a fix received from any source (typically the MapLibre
GeolocateControl). Capture flow prefers this over a fresh native call
because the control's watchPosition is already running. Notifies
subscribers synchronously so the UI can refresh distance pills.
*/
export function recordFix(inFix: FreshFix): void
{
	fLatestFix = inFix
	fPermissionDenied = false  // un fix qui arrive prouve que la permission est (re)accordée
	// Persist to disk so the next cold start can centre the map on
	// the user's last known location (parity with Android, where
	// LocationProvider exposes `currentLocation.value` immediately at
	// boot). Wrapped in try/catch: localStorage can throw in private
	// browsing mode on iOS.
	try { localStorage.setItem(kCachedFixStorageKey, JSON.stringify(inFix)) } catch { /* private mode / quota */ }
	for (const vFn of fListeners) vFn(inFix)
}

/*
Read the most recent fix without triggering a fresh OS call. Returns
null when no fix has ever been recorded this session OR when the last
one is older than kMaxGpsAgeMs — parité Android `captureAvailability`
(`currentLocation?.takeIf { ageMs() <= MAX_GPS_AGE_MS }`). No accuracy
filter: Android has none, the proximity gate (distance ≤ 30 m) is the
only precision check that matters.
*/
export function getLatestFix(): FreshFix | null
{
	if (!fLatestFix) return null
	if (Date.now() - fLatestFix.timestamp > kMaxGpsAgeMs) return null
	return fLatestFix
}

/*
Last observed fix WITHOUT any TTL or accuracy filtering. Use for UI
that wants to centre on / display "where the user was last seen"
regardless of freshness — `getLatestFix` (filtered) stays for distance
and proximity gates that need a precise read. Returns null only when
no fix has ever been recorded this session.
*/
export function getRawFix(): FreshFix | null
{
	return fLatestFix
}

export const kRecentFixMaxAgeMs: number = 15 * 60_000  // parité Android MAX_BOOTSTRAP_FIX_AGE_MS (LocationProvider.kt)

/*
Fraîcheur d'un fix pour le cadrage carte (seed, recadrage auto, FAB
localisation) — parité Android `Location.isRecentFix()`. Fenêtre bien
plus large que kMaxGpsAgeMs (gates de capture) : recentrer sur une
position de ≤ 15 min est utile, sur celle d'hier c'est pire qu'attendre
un fix réel. Âge négatif (timestamp dans le futur — horloge système
suspecte) = périmé, comme la borne basse 0 du range Android.
*/
export function isRecentFix(inFix: FreshFix | null, inMaxAgeMs: number = kRecentFixMaxAgeMs): inFix is FreshFix
{
	if (!inFix) return false
	const vAgeMs = Date.now() - inFix.timestamp
	return vAgeMs >= 0 && vAgeMs <= inMaxAgeMs
}

/*
Read the disk-cached fix from a previous session. Independent of
`getLatestFix` (which enforces a 30 s freshness window — too strict
for cold start). Used by the map mount to centre on the user's last
known location before the GPS produces a fresh fix.
*/
export function getCachedFix(inMaxAgeMs: number = kCachedFixTtlMs): FreshFix | null
{
	try
	{
		const vRaw = localStorage.getItem(kCachedFixStorageKey)
		if (!vRaw) return null
		const vFix = JSON.parse(vRaw) as FreshFix
		if (typeof vFix?.lat !== 'number' || typeof vFix?.lon !== 'number') return null
		// Borne [0..max] comme isRecentFix : un timestamp dans le futur
		// (horloge système reculée) ou corrompu (NaN) = fix périmé.
		const vAgeMs = Date.now() - vFix.timestamp
		if (!(vAgeMs >= 0 && vAgeMs <= inMaxAgeMs)) return null
		return vFix
	}
	catch
	{
		return null
	}
}

/*
Refus de permission (PERMISSION_DENIED, code 1) observé par n'importe
quel chemin (watch permanent ou one-shot FAB). Surfacé par MapScreen en
toast — miroir de la snackbar Android « Permission de localisation
refusée » (MapScreen.kt, permissionLauncher). Sur iOS un refus antérieur
ne re-déclenche jamais l'invite (seule issue : Réglages), l'échec serait
muet sans cette remontée. Réarmé dès qu'un fix aboutit.
*/
export function isGeolocationDenied(): boolean
{
	return fPermissionDenied
}

/*
Subscribe to permission-denial events. Fires immediately when already
denied (replay, même contrat que subscribeFix), then on every
false→true transition. Returns an unsubscribe closure.
*/
export function subscribeDenied(inFn: () => void): () => void
{
	fDeniedListeners.add(inFn)
	if (fPermissionDenied) inFn()
	return () => { fDeniedListeners.delete(inFn) }
}

function recordDenied(): void
{
	if (fPermissionDenied) return  // déjà signalé — pas de re-notification
	fPermissionDenied = true
	for (const vFn of fDeniedListeners) vFn()
}

/*
Boot the geolocation pipeline as a permanent `watchPosition`. Mirrors
Android `LocationProvider.start()` (never explicitly stopped) — one
watch lives for the lifetime of the tab, every fix feeds `recordFix`
which in turn drives the custom user-location layer + the cached
distance widgets.

Rationale: relying on MapLibre's `GeolocateControl.trigger()`
to start watchPosition was failing silently on Vanadium without a user
gesture. Starting the watch ourselves at boot (or at the post-onboarding
"Commencer" tap, which IS a user gesture) sidesteps that constraint and
keeps a fresh fix permanently in cache.

Idempotent: `fWatchId` guards against double-start when both `main.ts`
and `WelcomeScreen.ts` call into this. Errors are logged but do NOT
clear the watch — Chromium's geolocation impl retries internally; a
manual teardown would forfeit any chance of reprise auto.
*/
let fWatchId: number | null = null  // active watchPosition handle, null until first bootstrap

export function bootstrapGeolocation(): void
{
	if (!('geolocation' in navigator)) return
	if (fWatchId !== null) return  // already running — keep it alive across redundant calls
	try
	{
		fWatchId = navigator.geolocation.watchPosition(
			(inPos) => {
				recordFix({
					lat: inPos.coords.latitude,
					lon: inPos.coords.longitude,
					accuracyMeters: inPos.coords.accuracy,
					timestamp: inPos.timestamp,
				})
			},
			(inErr) => {
				// Codes: 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT.
				// Do NOT clear the watch — Chromium retries internally and
				// will fire success once the user accepts / signal returns.
				console.warn(`geolocation watch error code=${inErr.code} msg=${inErr.message ?? ''}`)
				if (inErr.code === 1) recordDenied()
			},
			{ enableHighAccuracy: true, maximumAge: 10_000, timeout: 30_000 },
		)
	}
	catch (vErr)
	{
		// Some embedded webviews throw synchronously on watchPosition; soft-fail.
		console.warn('watchPosition threw synchronously', vErr)
	}
}

/*
Subscribe to fix updates. Callback fires once immediately with the
current RAW fix (may be null) and again on every recordFix() call —
both unfiltered, so the attach replay and the live notifications carry
the same kind of value. Per-consumer gates (e.g. the capture button's
age check) are applied by the subscriber. Returns an unsubscribe
closure — call it on screen unmount / sheet close.
*/
export function subscribeFix(inFn: (inFix: FreshFix | null) => void): () => void
{
	fListeners.add(inFn)
	inFn(getRawFix())
	return () => { fListeners.delete(inFn) }
}

/*
Request a fresh GPS fix.

Strategy:
1. If a cached fix is fresh AND accurate enough → return it immediately.
2. Otherwise, open a `watchPosition` and stream fixes until either:
   • accuracy ≤ kMinAccuracyMeters → settle with that fix, or
   • kPollTimeoutMs elapsed → settle with the BEST fix observed so far
     (smallest accuracy), even if it exceeds the floor — the capture
     flow's haversine check against the target arbre still gates the
     actual proximity, so a coarse-but-near fix remains usable. We
     only reject when no fix at all has been received during the window.

The polling pattern replaces the single-shot getCurrentPosition that
shipped a "Fix too coarse" toast prematurely: on device under canopy
the 1st fix arrives at 40-80 m and converges to <30 m in 3-8 s.
*/
export async function requestFreshFix(): Promise<FreshFix>
{
	const vCached = pickCached()  // fresh-enough cached fix, null otherwise
	if (vCached) return vCached

	if (!('geolocation' in navigator))
	{
		throw new Error('Geolocation API not available')
	}

	return new Promise<FreshFix>((inResolve, inReject) => {
		let vSettled = false  // single-fire guard for the resolver/rejector pair
		let vBest: FreshFix | null = null  // best fix observed so far (smallest accuracy)
		let vWatchId: number | null = null  // watchPosition handle, cleared on settle

		const vSettle = (inFn: () => void): void => {
			if (vSettled) return
			vSettled = true
			if (vWatchId !== null)
			{
				try { navigator.geolocation.clearWatch(vWatchId) } catch { /* already cleared */ }
				vWatchId = null
			}
			window.clearTimeout(vOuterTimer)
			inFn()
		}

		// Outer JS-level poll window. Closes the watch and falls back to
		// the best fix observed so far — degraded mode rather than a hard
		// reject.
		const vOuterTimer = window.setTimeout(() => {
			if (vBest !== null)
			{
				recordFix(vBest)  // cache the best-effort fix for the next caller
				const vAccepted = vBest
				vSettle(() => inResolve(vAccepted))
			}
			else
			{
				vSettle(() => inReject(new Error('Fix too stale: timeout')))
			}
		}, kPollTimeoutMs)

		try
		{
			vWatchId = navigator.geolocation.watchPosition(
				(inPos) => {
					const vAccuracy: number = inPos.coords.accuracy  // meters
					const vFix: FreshFix = {
						lat: inPos.coords.latitude,
						lon: inPos.coords.longitude,
						accuracyMeters: vAccuracy,
						timestamp: inPos.timestamp,
					}
					if (vBest === null || vAccuracy < vBest.accuracyMeters) vBest = vFix
					if (vAccuracy <= kMinAccuracyMeters)
					{
						recordFix(vFix)
						vSettle(() => inResolve(vFix))
					}
				},
				(inErr) => {
					// Refus marqué AVANT le reject : le caller discrimine via
					// isGeolocationDenied(), le message OS étant localisé.
					if (inErr.code === 1) recordDenied()
					vSettle(() => inReject(new Error(inErr.message || 'Geolocation failed')))
				},
				{ enableHighAccuracy: true, maximumAge: 0, timeout: kPollTimeoutMs },
			)
		}
		catch (vErr)
		{
			vSettle(() => inReject(new Error(vErr instanceof Error ? vErr.message : 'watchPosition threw')))
		}
	})
}

/*
Return the cached fix if it satisfies freshness + accuracy. Otherwise
null — caller should fall back to the native API. The accuracy check
is legitimate HERE (and only here): it decides whether the one-shot
convergence poll is worth running, it never gates UI availability.
*/
function pickCached(): FreshFix | null
{
	if (!fLatestFix) return null
	if (Date.now() - fLatestFix.timestamp > kMaxGpsAgeMs) return null
	if (fLatestFix.accuracyMeters > kMinAccuracyMeters) return null
	return fLatestFix
}

/*
Great-circle distance between two WGS84 points, in meters. Standard
haversine — accurate enough for the < 100 m proximity checks used by
the capture flow + sheet distance widget.
*/
export function haversineMeters(inLat1: number, inLon1: number, inLat2: number, inLon2: number): number
{
	const vDLat = toRadians(inLat2 - inLat1)  // delta latitude in radians
	const vDLon = toRadians(inLon2 - inLon1)  // delta longitude in radians
	const vSlat = Math.sin(vDLat / 2)
	const vSlon = Math.sin(vDLon / 2)
	const vA = vSlat * vSlat + Math.cos(toRadians(inLat1)) * Math.cos(toRadians(inLat2)) * vSlon * vSlon
	return 2 * kEarthRadiusMeters * Math.asin(Math.min(1, Math.sqrt(vA)))
}

/*
Initial great-circle bearing from point 1 to point 2, in degrees
[0..360), 0 = geographic north. Mirror of the bearing that Android's
`Location.distanceBetween` returns (HuntPanel target blip). North-up by
design — no device-orientation sensor involved.
*/
export function initialBearingDegrees(inLat1: number, inLon1: number, inLat2: number, inLon2: number): number
{
	const vPhi1 = toRadians(inLat1)
	const vPhi2 = toRadians(inLat2)
	const vDLon = toRadians(inLon2 - inLon1)
	const vY = Math.sin(vDLon) * Math.cos(vPhi2)
	const vX = Math.cos(vPhi1) * Math.sin(vPhi2) - Math.sin(vPhi1) * Math.cos(vPhi2) * Math.cos(vDLon)
	return (Math.atan2(vY, vX) * 180 / Math.PI + 360) % 360
}

function toRadians(inDeg: number): number { return inDeg * Math.PI / 180 }
